import {
  BasePageV2,
  BasePageV2Body,
  BasePageV2Header,
  BasePageV2Navigation,
  NavigationCloseAction,
  ContentSize,
  LinearProgressProps,
} from '@fdha/web-ui-library';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { AuthStatus, useAuthStatus } from '@fdha/web-auth';
import { privatePageNameByRoute, publicPageNameByRoute } from '@routes';
import { onError } from '@apollo/client/link/error';
import { useApolloClient } from '@apollo/client';
import { SxProps, Theme } from '@mui/material';

export interface Progress {
  show: boolean;
  progressBarProps: LinearProgressProps;
}

interface Props {
  title?: string;
  i18nKeyTitle?: string;
  i18nTitleParams?: { [key: string]: string };
  i18nKeySubtitle?: string;
  i18nSubtitleParams?: { [key: string]: string };
  centeredTitle?: boolean;
  subtitle?: string;
  showBack?: boolean;
  showClose?: boolean;
  showHorizontalScroll?: boolean;
  showNavigation?: boolean;
  coverImageUrl?: string;
  contentSize?: ContentSize;
  currentRouteName?: {
    text?: string;
    i18nKey?: string;
    i18nParams?: { [key: string]: string | number };
  };
  parentRouteName?: {
    text?: string;
    i18nKey?: string;
    i18nParams?: { [key: string]: string | number };
  };
  progress?: Progress;
  isLoading?: boolean;
  rightButton?: JSX.Element;
  type?: 'default' | 'cover' | 'notFound';
  sx?: SxProps<Theme>;
  handleActionBeforeClose?: () => Promise<NavigationCloseAction>;
}

// Helper function to check if a route pattern matches an actual path
const routeMatches = (routePattern: string, actualPath: string): boolean => {
  const routeSegments = routePattern.split('/').filter((x) => x);
  const pathSegments = actualPath.split('/').filter((x) => x);

  if (routeSegments.length !== pathSegments.length) {
    return false;
  }

  return routeSegments.every((segment, index) => {
    // Dynamic segments start with [:
    if (segment.startsWith('[:')) {
      return true; // Any value matches
    }
    return segment === pathSegments[index];
  });
};

const DefaultBasePage: React.FC<PropsWithChildren<Props>> = ({
  title,
  i18nKeyTitle,
  i18nTitleParams,
  i18nKeySubtitle,
  i18nSubtitleParams,
  centeredTitle = false,
  subtitle,
  showBack = true,
  showClose = false,
  showHorizontalScroll = true,
  showNavigation = true,
  coverImageUrl,
  contentSize = 'xlarge',
  currentRouteName,
  parentRouteName,
  isLoading,
  progress,
  rightButton,
  type,
  sx,
  children,
  handleActionBeforeClose,
}) => {
  const authStatus = useAuthStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  const pageNamesByRoute = useMemo(() => {
    const routes =
      authStatus === AuthStatus.SIGNED_IN
        ? privatePageNameByRoute
        : publicPageNameByRoute;

    // If parentRouteName is provided, override the parent route
    if (parentRouteName) {
      const pathSegments = location.pathname.split('/').filter((x) => x);

      // Get parent path by removing the last segment
      if (pathSegments.length > 0) {
        const parentPath = `/${pathSegments.slice(0, -1).join('/')}`;

        return routes.map((route) => {
          // If this route matches the parent path, override it
          if (routeMatches(route.route, parentPath)) {
            return {
              ...route,
              name: parentRouteName.text || route.name,
              i18nKey: parentRouteName.i18nKey,
            };
          }
          return route;
        });
      }
    }

    return routes;
  }, [authStatus, parentRouteName, location.pathname]);

  const errorControl = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions }) => {
        if (!extensions.extensions) {
          return;
        }
        const codeExtensions = extensions.extensions as { code?: string };

        if (codeExtensions.code === 'NOT_FOUND') {
          setIsNotFound(true);
        }
      });
    }
  });

  client.setLink(errorControl.concat(client.link));

  const notFoundRedirect = () => navigate('/');

  const handleBack = () => {
    navigate('../');
  };

  const shouldShowHeader = showBack || subtitle || title || rightButton;
  const shouldShowNavigation = showNavigation || showClose || progress?.show;
  const typeByProps = isNotFound
    ? 'notFound'
    : coverImageUrl
    ? 'cover'
    : 'default';

  return (
    <BasePageV2
      type={type || typeByProps}
      contentSize={contentSize}
      coverImageUrl={coverImageUrl}
      showHorizontalScroll={showHorizontalScroll}
      handleNotFoundRedirect={notFoundRedirect}
      sx={sx}
    >
      {shouldShowNavigation && (
        <BasePageV2Navigation
          type={showNavigation ? 'breadcrumb' : 'close'}
          breadcrumbProps={{
            currentRouteName,
            pageNamesByRoute,
            pathname: location.pathname,
            onClick: (route) => navigate(route),
          }}
          closeProps={{
            onBeforeClose: handleActionBeforeClose,
            onClose: handleBack,
          }}
          progress={progress?.show ? progress?.progressBarProps : undefined}
          isLoading={isLoading}
        />
      )}
      {shouldShowHeader && (
        <BasePageV2Header
          title={title}
          i18nKeyTitle={i18nKeyTitle}
          i18nTitleParams={i18nTitleParams}
          i18nKeySubtitle={i18nKeySubtitle}
          i18nSubtitleParams={i18nSubtitleParams}
          subtitle={subtitle}
          showBack={showBack}
          onClickBack={handleBack}
          centeredHeader={centeredTitle}
          rightButton={rightButton}
          isLoading={isLoading}
        />
      )}
      <BasePageV2Body>{children}</BasePageV2Body>
    </BasePageV2>
  );
};

export default DefaultBasePage;
